import Container from 'react-bootstrap/Container';
import Nav from 'react-bootstrap/Nav';
import Navbar from 'react-bootstrap/Navbar';
import NavDropdown from 'react-bootstrap/NavDropdown';
import Badge from 'react-bootstrap/Badge';
import { useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {LinkContainer} from 'react-router-bootstrap';
import useUserStore from '../stores/user-store';
import { useCurrentAccountQuery, useLogoutMutation } from '../new/auth.query';
import { clearAuthSession } from '../new/auth-session';
import { hasPermission, hasAnyPermission, permissionPaths } from '../auth/permission-paths';


function MainNavbar() {
  const navigate = useNavigate();
  const location = useLocation();
  const {username, passkeyConfigured, permissions} = useUserStore(state => ({
    username: state.username,
    passkeyConfigured: state.passkeyConfigured,
    permissions: state.permissions
  }));
  const currentAccountQuery = useCurrentAccountQuery(true);
  const logoutMutation = useLogoutMutation();
  const showSecurityIndicator = !passkeyConfigured;
  const canViewClientSearch = hasPermission(permissions, permissionPaths.clientSearch.display);
  const canViewReports = hasPermission(permissions, permissionPaths.reports.display);
  const canManageUsers = hasPermission(permissions, permissionPaths.administration.manageUsers);
  const canManageRoles = hasPermission(permissions, permissionPaths.administration.manageRoles);
  const canViewAuditLogs = hasPermission(permissions, permissionPaths.administration.viewAuditLogs);
  const canViewErrorLogs = hasPermission(permissions, permissionPaths.administration.viewErrorLogs);
  const canViewLogs = canViewAuditLogs || canViewErrorLogs;
  const canManageGroupApplicationStatus = hasPermission(permissions, permissionPaths.applicationHistory.update);
  const canViewAdmin = hasAnyPermission(permissions, [
    permissionPaths.administration.manageUsers,
    permissionPaths.administration.manageRoles,
    permissionPaths.applicationHistory.update
  ]);
  const passwordExpiryIndicator = useMemo(() => {
    const account = currentAccountQuery.data;

    if (!account?.passwordChangeAvailable) {
      return null;
    }

    if (account.passwordExpired) {
      return {
        label: "Password Expired",
        bg: "danger",
        text: "light"
      };
    }

    if (typeof account.passwordExpiresInDays === "number" && account.passwordExpiresInDays <= 7) {
      return {
        label: account.passwordExpiresInDays === 0 ? "Password Today" : `Password ${account.passwordExpiresInDays}d`,
        bg: "warning",
        text: "dark"
      };
    }

    return null;
  }, [currentAccountQuery.data]);

  const handleLogout = async () => {
    try {
      await logoutMutation.mutateAsync();
    } catch {
      clearAuthSession();
    }

    const returnTo = `${location.pathname ?? ""}${location.search ?? ""}${location.hash ?? ""}` || "/";
    navigate(`/Auth/Login?returnTo=${encodeURIComponent(returnTo)}`, { replace: true });
  };

  return (
    <Navbar expand="lg" className="bg-body-tertiary">
        <Container fluid>
          <LinkContainer to="/">
            <Navbar.Brand>MFP</Navbar.Brand>
          </LinkContainer>
        <Navbar.Toggle aria-controls="basic-navbar-nav" />
        <Navbar.Collapse id="basic-navbar-nav">
          <Nav className="me-auto">
            <LinkContainer to="/">
              <Nav.Link>Home</Nav.Link>
            </LinkContainer>
            {canViewClientSearch ? (
              <LinkContainer to="/Client/ClientSearch">
                <Nav.Link>Client Search</Nav.Link>
              </LinkContainer>
            ) : null}
            {canViewReports ? (
              <LinkContainer to="/Reports/ViewReports">
                <Nav.Link>Reports</Nav.Link>
              </LinkContainer>
            ) : null}
            {canViewAdmin ? (
              <NavDropdown title="Admin">
                {canManageUsers ? (
                  <LinkContainer to="/Admin/ManageUsers">
                    <NavDropdown.Item>Manage Users</NavDropdown.Item>
                  </LinkContainer>
                ) : null}
                {canManageRoles ? (
                  <LinkContainer to="/Admin/ManageRoles">
                    <NavDropdown.Item>Manage Roles</NavDropdown.Item>
                  </LinkContainer>
                ) : null}
                {canManageGroupApplicationStatus ? (
                  <LinkContainer to="/Admin/ManageGroupApp">
                    <NavDropdown.Item>Edit/Update Group App Status</NavDropdown.Item>
                  </LinkContainer>
                ) : null}
              </NavDropdown>
            ) : null}
            {canViewLogs ? (
              <NavDropdown title="Logs">
                {canViewAuditLogs ? (
                  <LinkContainer to="/Admin/AuditLogs">
                    <NavDropdown.Item>Audit Logs</NavDropdown.Item>
                  </LinkContainer>
                ) : null}
                {canViewErrorLogs ? (
                  <LinkContainer to="/Admin/ErrorLogs">
                    <NavDropdown.Item>Error Logs</NavDropdown.Item>
                  </LinkContainer>
                ) : null}
              </NavDropdown>
            ) : null}
          </Nav>
          <Nav>
            <NavDropdown
              title={(
                <span className="d-inline-flex align-items-center gap-2">
                  <span>{username}</span>
                  {showSecurityIndicator ? (
                    <Badge bg="warning" text="dark">
                      Security
                    </Badge>
                  ) : null}
                  {passwordExpiryIndicator ? (
                    <Badge bg={passwordExpiryIndicator.bg} text={passwordExpiryIndicator.text}>
                      {passwordExpiryIndicator.label}
                    </Badge>
                  ) : null}
                </span>
              )}
              align="end"
            >
              <LinkContainer to="/Account/Settings">
                <NavDropdown.Item>
                  <span className="d-inline-flex align-items-center gap-2">
                    <span>Account Settings</span>
                    {showSecurityIndicator ? (
                      <Badge bg="warning" text="dark">
                        Recommended
                      </Badge>
                    ) : null}
                    {passwordExpiryIndicator ? (
                      <Badge bg={passwordExpiryIndicator.bg} text={passwordExpiryIndicator.text}>
                        {passwordExpiryIndicator.label}
                      </Badge>
                    ) : null}
                  </span>
                </NavDropdown.Item>
              </LinkContainer>
              <NavDropdown.Item onClick={handleLogout}>
                Sign Out
              </NavDropdown.Item>
            </NavDropdown>
          </Nav>
        </Navbar.Collapse>
        </Container>
    </Navbar>
  );
}

export default MainNavbar;
